import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
    getRecoveryCount,
    subscribeConnectionState,
} from '~/backend/connectionState';
import { useConnectionState } from './useConnectionState';

/** How long "Connection restored" stays up. */
export const RESTORED_NOTICE_MS = 4_000;

/**
 * What the UI says about the connection: an interruption while it lasts,
 * `restored` for a moment after one ends with the server reached again,
 * and nothing otherwise. An interruption whose requests all gave up or
 * were cancelled ends without a notice: the connection was not seen to
 * come back, and each request reports its own failure.
 */
export type ConnectionNotice = 'offline' | 'reconnecting' | 'restored' | null;

/** The connection notice to show, re-rendering as it changes. */
export function useConnectionNotice(): ConnectionNotice {
    const state = useConnectionState();
    const recoveries = useSyncExternalStore(
        subscribeConnectionState,
        getRecoveryCount,
        () => 0
    );
    const [restored, setRestored] = useState(false);
    const seenRecoveries = useRef(recoveries);

    useEffect(() => {
        if (state !== 'connected') {
            // Back under interruption, or the browser came online while
            // requests are still retrying: nothing is restored yet.
            seenRecoveries.current = recoveries;
            setRestored(false);
            return undefined;
        }
        if (recoveries === seenRecoveries.current) {
            return undefined;
        }
        seenRecoveries.current = recoveries;
        setRestored(true);
        const timer = setTimeout(() => setRestored(false), RESTORED_NOTICE_MS);
        return () => clearTimeout(timer);
    }, [state, recoveries]);

    if (state !== 'connected') {
        return state;
    }
    return restored ? 'restored' : null;
}
